import { Box, Grid, Typography } from "@mui/material";
import RopCollapsibleTable from "../../../../main/components/RopCollapsibleTable/RopCollapsibleTable";
import { CollapsibleTableRowType } from "../../../../main/components/RopCollapsibleTable/RopTableCollapsibleBody";
import { TableItem, TableRowType } from "../../../../main/components/RopTable/RopTable";
import { formatNumberPercent } from "../../../dashboard/helpers/format-number";
import { ClusterPerformance } from "../../types";

export type PerformancePerAssetTableProps = {
    clusters: ClusterPerformance[];
    loading: boolean;
    selectedClusterName?: string;
    inverterAvailabilityExpectedValue?: number;
}

export default function PerformancePerAssetTable(props: PerformancePerAssetTableProps) {

    const clusters = getOrderedClusters(props.clusters);

    const items: CollapsibleTableRowType[] = clusters.map((cluster) => {

        const rowItems: TableItem[] = [
            {
                item: cluster.clusterName,
                align: "center",
                isMobile: true,
                width: "13%",
            },
            {
                item: cluster.powerPlants.length > 0 ? cluster.powerPlants[0].generationType : "N/A",
                align: "center",
                isMobile: true,
                width: "8%",
            },
            {
                item: Number(cluster.irradiance).toFixed(2) || "0.00",
                align: "center",
                isMobile: true,
                width: "10%",
            },
            {
                item: Number(cluster.generatedEnergy).toFixed(2) || "0.00",
                align: "center",
                isMobile: true,
                width: "10%",
            },
            {
                item: cluster.expectedEnergy > 0 ? Number(cluster.expectedEnergy).toFixed(2) || "0.00" : "N/A",
                align: "center",
                isMobile: true,
                width: "10%",
            },
            {
                item: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: "14px", color: cluster.deviationPercent <= 0 ? "#10B981" : "#EF4444" }}>
                            {cluster.deviationPercent < 0 ? "▲" : "▼"}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: cluster.deviationPercent <= 0 ? "#10B981" : "#EF4444",
                                fontWeight: "500"
                            }}
                        >
                            {cluster.deviationPercent < 0 ? "+" : ""}{Math.abs(Number(cluster.deviationPercent)).toFixed(1)}
                        </Typography>
                    </Box>
                ),
                align: "center",
                isMobile: true,
                width: "10%",
            },
            {
                item: (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: cluster.performanceRatio >= cluster.expectedPerformanceRatio ? "#10B981" : "#EF4444",
                            fontWeight: "500"
                        }}
                    >
                        {cluster.performanceRatio ? formatNumberPercent(Number(cluster.performanceRatio) ?? 0, 2) || "0,00" : "N/A"}
                    </Typography>
                ),
                align: "center",
                isMobile: true,
                width: "10%",
            },
            {
                item: (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: cluster.availability >= (props.inverterAvailabilityExpectedValue ?? 0) ? "#10B981" : "#EF4444",
                            fontWeight: "500"
                        }}
                    >
                        {cluster.availability ? formatNumberPercent(Number(cluster.availability) ?? 0, 2) || "0,00" : "N/A"}
                    </Typography>
                ),
                align: "center",
                isMobile: true,
                width: "10%",
            },
        ]

        const historyItems: TableRowType[] = cluster.powerPlants.map((powerPlant) => {
            return {
                rowItems: [
                    {
                        item: <></>,
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: powerPlant.usinaName,
                        align: "center",
                        isMobile: true,
                        width: "13%",
                    },
                    {
                        item: powerPlant.generationType || "N/A",
                        align: "center",
                        isMobile: true,
                        width: "8%",
                    },
                    {
                        item: Number(powerPlant.irradiance).toFixed(2) || "0.00",
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: Number(powerPlant.generatedEnergy).toFixed(2) || "0.00",
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: powerPlant.expectedEnergy > 0 ? Number(powerPlant.expectedEnergy).toFixed(2) || "0.00" : "N/A",
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px", justifyContent: "center" }}>
                                <Typography sx={{ fontSize: "14px", color: powerPlant.deviationPercent <= 0 ? "#10B981" : "#EF4444" }}>
                                    {powerPlant.deviationPercent < 0 ? "▲" : "▼"}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        color: powerPlant.deviationPercent <= 0 ? "#10B981" : "#EF4444",
                                        fontWeight: "500"
                                    }}
                                >
                                    {powerPlant.deviationPercent < 0 ? "+" : ""}{Math.abs(Number(powerPlant.deviationPercent)).toFixed(1)}
                                </Typography>
                            </Box>
                        ),
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: (
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: powerPlant.performanceRatio >= powerPlant.expectedPerformanceRatio ? "#10B981" : "#EF4444",
                                    fontWeight: "500"
                                }}
                            >
                                {powerPlant.performanceRatio ? formatNumberPercent(Number(powerPlant.performanceRatio) ?? 0, 2) || "0,00" : "N/A"}
                            </Typography>
                        ),
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                    {
                        item: (
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: powerPlant.availability >= (props.inverterAvailabilityExpectedValue ?? 0) ? "#10B981" : "#EF4444",
                                    fontWeight: "500"
                                }}
                            >
                                {powerPlant.availability ? formatNumberPercent(Number(powerPlant.availability) ?? 0, 2) || "0,00" : "N/A"}
                            </Typography>
                        ),
                        align: "center",
                        isMobile: true,
                        width: "10%",
                    },
                ],
            }
        })

        return {
            rowItems: rowItems,
            historyHeaders: [],
            historyRows: historyItems
        }
    })

    const itemsHeader = [
        {
            item: (<></>
            ),
            width: "10%",
            align: "center",
            isMobile: true
        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Cluster/</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Ativo</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "13%",
        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Tipo</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "8%",
        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Irradiância</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(kWh/m²)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",

        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Energia Gerada</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(MWh)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",

        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Energia Esperada</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(MWh)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",

        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Desvio</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(%)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",
        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>PR</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(%)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",
        },
        {
            item: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>Disponibilidade</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>(%)</Typography>
                </Box>
            ),
            align: "center",
            isMobile: true,
            width: "10%",
        },
    ]

    return (
        <Grid item xs={12} >
            <RopCollapsibleTable
                sxCardBox={{ marginLeft: { xs: "10px", md: "0px" }, width: "100%" }}
                hover
                itemsHeaders={itemsHeader as TableItem[]}
                items={items}
                isLoading={props.loading}
                historyTableColSpan={itemsHeader.length}
                saveCollapsable
                initialSelectedItem={`row${props.selectedClusterName}`}
            />
        </Grid>
    )
}

function getOrderedClusters(clusters: ClusterPerformance[]): ClusterPerformance[] {
    const clusterWithoutUsinasSemCluster = clusters.filter(cluster => cluster.clusterName !== "Usinas sem Cluster");
    const usinasSemCluster = clusters.find(cluster => cluster.clusterName === "Usinas sem Cluster");
    const orderedClusters = clusterWithoutUsinasSemCluster.sort((a, b) => a.clusterName.localeCompare(b.clusterName));

    return [...orderedClusters, ...(usinasSemCluster ? [usinasSemCluster] : [])];
}
